/**
 * RIM — Rollover Flow (5-step wizard).
 *
 * Wave 5.2 IRA-FN-ROLLOVERS port:
 *   The wizard now POSTs to /v3/ira/rollovers (Function-side compliance)
 *   instead of going directly to jXRL via IraCoreService. The Function
 *   enforces:
 *     • IRC §408(d)(3)(B) one-rollover-per-12-months (per OwnerCifId,
 *       indirect kind only — direct + trustee-to-trustee + Roth conversion
 *       are EXEMPT)
 *     • SIMPLE-IRA 2-year wait (destination=SIMPLE, source=Trad,
 *       sourcePlanCreatedAt < 730d ago)
 *     • Auto-expire stale prior pending indirect rollovers (so the 12-mo
 *       lookback doesn't over-reject)
 *     • IraTransactionCodes scenario lookup (catalog ships EMPTY → friendly
 *       admin-link banner)
 *
 * Surfaces 4 server error codes inline:
 *   ONE_ROLLOVER_LIMIT_EXCEEDED — owner already did an indirect rollover
 *                                 within the last 12 months
 *   SIMPLE_2YR_NOT_MET           — SIMPLE source < 2 years old
 *   60_DAY_DEADLINE_PASSED       — finalizing past day 60
 *   TC_NOT_CONFIGURED            — admin must seed IraTransactionCodes
 *
 * Banners (per port-plan §7):
 *   • 60-day countdown banner when the post resolves to a `pending` indirect
 *     rollover (server returns deadlineAt).
 *   • SIMPLE 2-year banner pre-flight when destination=SIMPLE and source=Trad —
 *     server-enforced reject backed up by client warning.
 */
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ArrowLeft } from 'lucide-react';
import { Button, CardRadioGroup, KVPair, Pill, Stepper, WidgetCard } from '@/components/primitives';
import { EmptyState } from '@/components/common/EmptyState';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { useAuth } from '@/contexts/AuthContext';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useIraDetail } from '../hooks/useIraDetail';
import { useEmitEvent } from '../hooks/useEmitEvent';
import { IRA_PLAN_TYPES, type IraPlanCode } from '../constants';
import type {
  IraRolloverKind,
  IraRolloverSourceKind,
  IraRolloverErrorCode,
  IraRolloverPostedData,
} from '@retail-ops/shared-types';
import '../styles/rim.css';

interface RolloverDraft {
  kind: IraRolloverKind;
  sourceKind: IraRolloverSourceKind;
  sourceInstitutionName: string;
  sourcePlanNumber: string;
  /** Receiving IRA type — used for SIMPLE-2yr client preflight banner. */
  receivingIraType: IraPlanCode;
  amount: string;
  form1099Year: number;
  form5498Year: number;
}

const STEPS = [
  { label: 'Rollover kind' },
  { label: 'Source institution' },
  { label: 'Receiving IRA' },
  { label: 'Amount & years' },
  { label: 'Review & submit' },
];

/**
 * Friendly inline messages for the 4 server error codes (port-plan §3.2.8 +
 * task spec). Other codes fall through to the raw server message.
 */
const ROLLOVER_ERROR_TEXT: Record<IraRolloverErrorCode, string> = {
  ONE_ROLLOVER_LIMIT_EXCEEDED:
    'Owner already completed a 60-day indirect rollover within the last 12 months. Per IRC §408(d)(3)(B), only one indirect IRA-to-IRA rollover is permitted per 12-month period across all IRAs.',
  SIMPLE_2YR_NOT_MET:
    'SIMPLE-IRA destinations require the source Traditional IRA to be open for at least 2 years. The IRS SIMPLE 2-year participation rule blocks this rollover until the wait clears.',
  '60_DAY_DEADLINE_PASSED':
    'The 60-day window for this indirect rollover has elapsed. Funds that arrive after day 60 become a taxable distribution.',
  TC_NOT_CONFIGURED:
    'Transaction-code sequence pending. An administrator must seed the IRA Transaction Codes catalog before this rollover can post (Admin → IRA → Transaction Codes).',
  INVALID_ROLLOVER_KIND: 'The selected rollover kind was not recognized by the server.',
  INVALID_SOURCE_KIND: 'The selected source kind was not recognized by the server.',
  MISSING_OWNER_CIF: 'Plan is missing the owner CIF — cannot post rollover.',
  MISSING_SOURCE_PLAN: 'Internal source plan number is required.',
  MISSING_SOURCE_INSTITUTION: 'External source institution name is required.',
  MISSING_PLAN_NUMBER: 'Destination plan number is required.',
  MISSING_OFFICER: 'Officer code is required.',
  INVALID_AMOUNT: 'Rollover amount must be a positive number.',
  PLAN_INACTIVE: 'Destination plan is not active in Core Director.',
  PLAN_NOT_FOUND: 'Destination plan was not found in Core Director.',
  POST_FAILED: 'Core Director rejected the rollover post.',
  INVALID_BODY: 'The rollover request body was invalid.',
};

/** Format an ISO timestamp as a "Xd Yh remaining until …" countdown. */
function formatDeadlineCountdown(deadlineIso: string): string {
  const deadline = new Date(deadlineIso).getTime();
  if (!Number.isFinite(deadline)) return deadlineIso;
  const now = Date.now();
  const ms = deadline - now;
  if (ms <= 0) return 'past deadline';
  const totalMinutes = Math.floor(ms / 60000);
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes - days * 60 * 24) / 60);
  if (days > 0) return `${days}d ${hours}h remaining`;
  if (hours > 0) return `${hours}h remaining`;
  return `< 1h remaining`;
}

export function RolloverFlow(): JSX.Element {
  const { planNumber } = useParams<{ planNumber: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const client = useApiClient();
  const emit = useEmitEvent();

  const { data: ira, loading: loadingIra, error: iraError } = useIraDetail(planNumber);

  const currentYear = new Date().getFullYear();
  const [step, setStep] = useState<number>(0);
  const [draft, setDraft] = useState<RolloverDraft>({
    kind: 'DIRECT',
    sourceKind: 'EXTERNAL_INSTITUTION',
    sourceInstitutionName: '',
    sourcePlanNumber: '',
    receivingIraType: 'TRAD',
    amount: '',
    form1099Year: currentYear,
    form5498Year: currentYear,
  });
  const [submitState, setSubmitState] = useState<'idle' | 'submitting' | 'submitted' | 'error'>('idle');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitErrorCode, setSubmitErrorCode] = useState<IraRolloverErrorCode | null>(null);
  const [postedData, setPostedData] = useState<IraRolloverPostedData | null>(null);

  const amountNum = Number.parseFloat(draft.amount);

  /**
   * Pre-flight client banners. The server is authoritative — these surface
   * before submission so the officer doesn't get to the review step only to
   * be rejected.
   */
  const simple2yrPreflight = useMemo(() => {
    // Banner condition: destination is SIMPLE OR receiving IRA is SIMPLE
    // (port-plan §7 client warning). Server enforces the actual reject —
    // this is informational so the officer pivots before submit.
    return draft.receivingIraType === 'SIMPLE'
      && draft.sourceKind === 'INTERNAL_PCB_PLAN';
  }, [draft.receivingIraType, draft.sourceKind]);

  if (!planNumber) return <EmptyState title="Plan number required" message="Open an IRA plan from RIM before starting a rollover." />;
  if (loadingIra) return <div className="rim-flow__loading"><LoadingSpinner label="Loading IRA plan…" size="lg" /></div>;
  if (iraError || !ira) {
    return (
      <EmptyState
        title="Unable to load IRA plan"
        message={iraError ?? 'Core Director did not return plan details.'}
        action={<Button variant="ghost" startIcon={<ArrowLeft size={14} />} onClick={() => navigate('..')}>Back to plan</Button>}
      />
    );
  }

  const canProceed = ((): boolean => {
    switch (step) {
      case 0:
        return draft.kind === 'DIRECT'
          || draft.kind === '60_DAY_INDIRECT'
          || draft.kind === 'TRUSTEE_TO_TRUSTEE'
          || draft.kind === 'ROTH_CONVERSION_FROM_TRAD'
          || draft.kind === 'SIMPLE_TO_TRAD_AFTER_2YR';
      case 1:
        if (draft.sourceKind === 'INTERNAL_PCB_PLAN') return draft.sourcePlanNumber.trim().length > 0;
        return draft.sourceInstitutionName.trim().length > 0;
      case 2: return !!draft.receivingIraType;
      case 3:
        return Number.isFinite(amountNum) && amountNum > 0
          && Number.isInteger(draft.form1099Year) && Number.isInteger(draft.form5498Year);
      case 4: return submitState === 'idle' || submitState === 'error';
      default: return false;
    }
  })();

  const submit = async (): Promise<void> => {
    if (!ira.cifId) {
      setSubmitError('Plan is missing the owner CIF — cannot post rollover.');
      setSubmitErrorCode('MISSING_OWNER_CIF');
      setSubmitState('error');
      return;
    }
    if (!user?.officerCode) {
      setSubmitError('Officer code is required to post the rollover.');
      setSubmitErrorCode('MISSING_OFFICER');
      setSubmitState('error');
      return;
    }
    setSubmitState('submitting');
    setSubmitError(null);
    setSubmitErrorCode(null);
    setPostedData(null);
    try {
      const resp = await client.retail.ira.postRollover({
        destinationPlanNumber: ira.planNumber,
        kind: draft.kind,
        sourceKind: draft.sourceKind,
        ...(draft.sourceKind === 'INTERNAL_PCB_PLAN'
          ? { sourcePlanNumber: draft.sourcePlanNumber.trim() }
          : { sourceInstitutionName: draft.sourceInstitutionName.trim() }),
        amount: amountNum,
        officerCode: user.officerCode,
        ownerCifId: ira.cifId,
        form1099Year: draft.form1099Year,
        form5498Year: draft.form5498Year,
      });
      if (!resp.success) {
        setSubmitErrorCode(resp.code);
        setSubmitError(ROLLOVER_ERROR_TEXT[resp.code] ?? resp.error);
        setSubmitState('error');
        return;
      }
      setPostedData(resp.data);
      emit({
        eventType: 'ira-rollover-posted',
        eventCategory: 'cif-account',
        severity: 'warn',
        context: `plan:${ira.planNumber}`,
        payload: {
          eventId: resp.data.eventId,
          planNumber: ira.planNumber,
          kind: draft.kind,
          sourceKind: draft.sourceKind,
          sourceInstitution: draft.sourceInstitutionName || undefined,
          sourcePlanNumber: draft.sourcePlanNumber || undefined,
          amount: amountNum,
          transactionCode: resp.data.transactionCode,
          status: resp.data.status,
          deadlineAt: resp.data.deadlineAt,
          form1099Year: resp.data.form1099Year,
          form5498Year: resp.data.form5498Year,
          form1099Box7Code: resp.data.form1099Box7Code,
          priorRolloverExpired: resp.data.priorRolloverExpired,
        },
      });
      setSubmitState('submitted');
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Rollover post failed.');
      setSubmitErrorCode(null);
      setSubmitState('error');
    }
  };

  return (
    <div className="rim-flow">
      <div className="rim-flow__crumb">
        <Button variant="ghost" startIcon={<ArrowLeft size={14} />} onClick={() => navigate('..')}>Back to plan</Button>
        <h1>IRA rollover · {ira.planDescription ?? ira.planCodeRaw} · {ira.planNumber}</h1>
      </div>

      <Stepper steps={STEPS} currentIndex={step} variant="apply" />

      {step === 0 && (
        <WidgetCard title="Rollover kind" stripe="compliance">
          <CardRadioGroup<IraRolloverKind>
            name="rim-rollover-kind"
            selected={draft.kind}
            onChange={(v) => setDraft({ ...draft, kind: v })}
            options={[
              { value: 'DIRECT',                    primary: 'Direct rollover (IRA → IRA, same type)',         helper: 'Trustee-to-trustee, no 60-day clock. Exempt from the 12-month rule.' },
              { value: '60_DAY_INDIRECT',           primary: '60-day (indirect) rollover',                      helper: 'Funds touched by owner. 60-day deadline + one-per-12-months rule applies.' },
              { value: 'TRUSTEE_TO_TRUSTEE',        primary: 'Trustee-to-trustee transfer (non-IRA → IRA)',     helper: 'e.g. 401(k) → Traditional IRA. Not a "rollover" per IRC; exempt.' },
              { value: 'ROTH_CONVERSION_FROM_TRAD', primary: 'Roth conversion from Traditional',                helper: 'Taxable conversion event. Exempt from the 12-month rule.' },
              { value: 'SIMPLE_TO_TRAD_AFTER_2YR',  primary: 'SIMPLE → Traditional (2-year wait satisfied)',    helper: 'Source SIMPLE plan must be open ≥ 2 years before this option becomes valid.' },
            ]}
          />
          {draft.kind === '60_DAY_INDIRECT' && (
            <div className="rim-rmd-banner is-warn" role="alert">
              <Pill variant="warn">60-day clock</Pill> Owner is subject to the one-rollover-per-12-months rule
              (IRC §408(d)(3)(B)). The server will reject if another indirect rollover for this CIF posted
              within the last 365 days.
            </div>
          )}
        </WidgetCard>
      )}

      {step === 1 && (
        <WidgetCard title="Source institution" stripe="compliance">
          <CardRadioGroup<IraRolloverSourceKind>
            name="rim-rollover-source-kind"
            selected={draft.sourceKind}
            onChange={(v) => setDraft({ ...draft, sourceKind: v })}
            options={[
              { value: 'EXTERNAL_INSTITUTION', primary: 'External institution',  helper: 'Funds arriving from outside PCB (Fidelity, Vanguard, Schwab, etc.).' },
              { value: 'INTERNAL_PCB_PLAN',    primary: 'Internal PCB plan',    helper: 'Source is another PCB IRA plan (cross-product moves, SIMPLE→Trad).' },
            ]}
          />
          <div className="rim-flow__grid-2">
            {draft.sourceKind === 'EXTERNAL_INSTITUTION' && (
              <label className="rim-flow__field">
                <span className="rim-flow__field-label">Institution name</span>
                <input
                  type="text"
                  className="rim-benef-field"
                  value={draft.sourceInstitutionName}
                  placeholder="e.g. Fidelity, Vanguard, Charles Schwab"
                  onChange={(e) => setDraft({ ...draft, sourceInstitutionName: e.currentTarget.value })}
                />
              </label>
            )}
            {draft.sourceKind === 'INTERNAL_PCB_PLAN' && (
              <label className="rim-flow__field">
                <span className="rim-flow__field-label">Source PCB plan number</span>
                <input
                  type="text"
                  className="rim-benef-field"
                  value={draft.sourcePlanNumber}
                  placeholder="10-digit plan number"
                  onChange={(e) => setDraft({ ...draft, sourcePlanNumber: e.currentTarget.value })}
                />
              </label>
            )}
          </div>
          {simple2yrPreflight && (
            <div className="rim-rmd-banner is-warn" role="alert">
              <Pill variant="warn">SIMPLE 2-yr wait</Pill> If the source plan is a Traditional IRA opened
              less than 2 years ago, the server will reject this rollover under the IRS SIMPLE
              2-year participation rule. Verify the source plan creation date before proceeding.
            </div>
          )}
        </WidgetCard>
      )}

      {step === 2 && (
        <WidgetCard title="Receiving IRA" stripe="compliance">
          <CardRadioGroup<IraPlanCode>
            name="rim-rollover-receiving"
            selected={draft.receivingIraType}
            onChange={(v) => setDraft({ ...draft, receivingIraType: v })}
            options={IRA_PLAN_TYPES.map((t) => ({
              value: t.code,
              primary: `${t.label} IRA`,
              helper: `Prefix ${t.prefix} · code ${t.code}`,
            }))}
          />
          <div className="rim-flow__hint">
            Tran code is resolved by the server from the IraTransactionCodes catalog (Q4/Q5 admin
            sequence). The catalog ships empty at launch — if it has not been seeded yet, the server
            returns <code>TC_NOT_CONFIGURED</code> with an admin link.
          </div>
        </WidgetCard>
      )}

      {step === 3 && (
        <WidgetCard title="Amount & tax-year stamping" stripe="compliance">
          <div className="rim-flow__grid-3">
            <label className="rim-flow__field">
              <span className="rim-flow__field-label">Amount (USD)</span>
              <input
                type="number"
                className="rim-benef-field"
                value={draft.amount}
                min={0}
                step={0.01}
                onChange={(e) => setDraft({ ...draft, amount: e.currentTarget.value })}
              />
            </label>
            <label className="rim-flow__field">
              <span className="rim-flow__field-label">1099-R year (if indirect)</span>
              <input
                type="number"
                className="rim-benef-field"
                value={draft.form1099Year}
                min={1900}
                max={9999}
                onChange={(e) => setDraft({ ...draft, form1099Year: Number.parseInt(e.currentTarget.value, 10) || draft.form1099Year })}
              />
            </label>
            <label className="rim-flow__field">
              <span className="rim-flow__field-label">5498 year</span>
              <input
                type="number"
                className="rim-benef-field"
                value={draft.form5498Year}
                min={1900}
                max={9999}
                onChange={(e) => setDraft({ ...draft, form5498Year: Number.parseInt(e.currentTarget.value, 10) || draft.form5498Year })}
              />
            </label>
          </div>
        </WidgetCard>
      )}

      {step === 4 && (
        <WidgetCard title="Review & submit" stripe="compliance">
          <div className="rim-kv-grid">
            <KVPair label="Plan" value={`${ira.planCodeRaw} · ${ira.planNumber}`} />
            <KVPair label="CIF" value={ira.cifId ?? '—'} />
            <KVPair label="Kind" value={draft.kind} />
            <KVPair label="Source kind" value={draft.sourceKind} />
            <KVPair
              label={draft.sourceKind === 'EXTERNAL_INSTITUTION' ? 'Source institution' : 'Source PCB plan'}
              value={draft.sourceKind === 'EXTERNAL_INSTITUTION' ? draft.sourceInstitutionName : draft.sourcePlanNumber}
            />
            <KVPair label="Receiving IRA" value={draft.receivingIraType} />
            <KVPair label="Amount" value={`$${amountNum.toFixed(2)}`} />
            <KVPair label="1099-R year" value={draft.form1099Year} />
            <KVPair label="5498 year" value={draft.form5498Year} />
          </div>
          {simple2yrPreflight && (
            <div className="rim-rmd-banner is-warn" role="alert">
              <Pill variant="warn">SIMPLE 2-yr wait</Pill> Server will reject if the source Traditional plan
              has not been open for ≥ 730 days.
            </div>
          )}
          {submitError && (
            <div className="rim-rmd-banner is-warn" role="alert">
              {submitErrorCode && (
                <Pill variant="warn">
                  {submitErrorCode === '60_DAY_DEADLINE_PASSED' ? '60-day passed'
                    : submitErrorCode === 'ONE_ROLLOVER_LIMIT_EXCEEDED' ? '12-mo limit'
                    : submitErrorCode === 'SIMPLE_2YR_NOT_MET' ? 'SIMPLE 2-yr'
                    : submitErrorCode === 'TC_NOT_CONFIGURED' ? 'TC catalog empty'
                    : submitErrorCode}
                </Pill>
              )}
              {' '}{submitError}
            </div>
          )}
          {submitState === 'submitted' && postedData && (
            <>
              <div className="rim-rmd-banner" role="status">
                Rollover posted via TaxPlnTranAdd · TC <strong>{postedData.transactionCode}</strong>
                {' '}· event <code>{postedData.eventId}</code> · status <strong>{postedData.status}</strong>.
                {postedData.priorRolloverExpired && (
                  <>{' '}A prior pending indirect rollover for this CIF was auto-marked <strong>expired</strong>
                  (its 60-day window had elapsed).</>
                )}
              </div>
              {postedData.deadlineAt && postedData.status === 'pending' && (
                <div className="rim-rmd-banner is-warn" role="alert">
                  <Pill variant="warn">60-day countdown</Pill>
                  {' '}Indirect rollover funds must arrive by{' '}
                  <strong>{new Date(postedData.deadlineAt).toLocaleString()}</strong>
                  {' '}({formatDeadlineCountdown(postedData.deadlineAt)}). Funds posted after the deadline
                  become a taxable distribution.
                </div>
              )}
              {postedData.form1099Box7Code && (
                <div className="rim-flow__hint">
                  1099-R box 7 will be stamped with code <strong>{postedData.form1099Box7Code}</strong>.
                  5498 box for {postedData.form5498Year} reporting.
                </div>
              )}
            </>
          )}
        </WidgetCard>
      )}

      <div className="rim-flow__actions">
        {step > 0 && submitState !== 'submitted' && (
          <Button variant="ghost" onClick={() => setStep(step - 1)}>Back</Button>
        )}
        {step < STEPS.length - 1 && (
          <Button variant="primary" disabled={!canProceed} onClick={() => setStep(step + 1)}>Next</Button>
        )}
        {step === STEPS.length - 1 && submitState !== 'submitted' && (
          <Button variant="primary" disabled={submitState === 'submitting' || !canProceed} onClick={() => { void submit(); }}>
            {submitState === 'submitting' ? 'Posting…' : 'Submit rollover'}
          </Button>
        )}
        {submitState === 'submitted' && (
          <Button variant="primary" onClick={() => navigate('..')}>Done</Button>
        )}
      </div>
    </div>
  );
}
